import React, { useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  User,
} from 'firebase/auth';
import { get, onValue, ref, update } from 'firebase/database';
import { auth, database } from './firebase';
import MessagesSection from './components/MessagesSection';
import PricingSection from './components/PricingSection';
import ProjectsSection from './components/ProjectsSection';
import ManualGenerationSection from './components/ManualGenerationSection';
import PromptTestSection from './components/PromptTestSection';
import BrandGenerationSection from './components/BrandGenerationSection';
import CreateProjectSection from './components/CreateProjectSection';
import EnqueueProjectSection from './components/EnqueueProjectSection';
import MetricsSection from './components/MetricsSection';
import ProjectRevenueSection from './components/ProjectRevenueSection';
import ClickAnalyticsSection from './components/ClickAnalyticsSection';
import './App.css';

type QueueNode = {
  active?: boolean;
  forceStop?: boolean;
  projectsGlobal?: unknown;
  projectsStandard?: unknown;
  run?: unknown;
};

type QueuePreviewNode = {
  active?: boolean;
  forceStop?: boolean;
  projects?: unknown;
  run?: unknown;
};

type LogCounts = {
  warning: number;
  error: number;
};

const sendBrandEmailEndpoint = 'https://enviar-email-slyn3k2c5q-uc.a.run.app';

const toQueueCount = (value: unknown): number => {
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === 'string' && item.trim().length > 0).length;
  }
  if (!value || typeof value !== 'object') {
    return 0;
  }
  return Object.values(value as Record<string, unknown>).filter(
    (item) => typeof item === 'string' && item.trim().length > 0
  ).length;
};

const getRunProjectInfo = (run: unknown): { projectId: string; step: number } | null => {
  if (!run || typeof run !== 'object') {
    return null;
  }

  const validEntries = Object.entries(run as Record<string, unknown>).filter(
    ([projectId, step]) => projectId.trim().length > 0 && typeof step === 'number' && Number.isFinite(step)
  );
  if (!validEntries.length) {
    return null;
  }

  const [projectId, rawStep] = validEntries[0];
  const step = rawStep as number;
  return { projectId, step };
};

const getLogCounts = (value: unknown): LogCounts => {
  if (!value || typeof value !== 'object') {
    return { warning: 0, error: 0 };
  }

  return Object.values(value as Record<string, unknown>).reduce<LogCounts>(
    (counts, rawEvent) => {
      if (!rawEvent || typeof rawEvent !== 'object') {
        return counts;
      }
      const severity = String((rawEvent as Record<string, unknown>).severity ?? '').trim().toUpperCase();
      if (severity === 'WARNING') {
        counts.warning += 1;
      }
      if (severity === 'ERROR') {
        counts.error += 1;
      }
      return counts;
    },
    { warning: 0, error: 0 }
  );
};

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState<
    | null
    | 'pricing'
    | 'prompt_test'
    | 'messages'
    | 'projects'
    | 'manual_generation'
    | 'create_project'
    | 'enqueue_project'
    | 'project_revenue'
    | 'brand_generation'
    | 'metrics'
    | 'click_analytics'
    | 'send_brand'
  >(null);
  const [queueData, setQueueData] = useState<QueueNode>({});
  const [queuePreviewData, setQueuePreviewData] = useState<QueuePreviewNode>({});
  const [logCounts, setLogCounts] = useState<LogCounts>({ warning: 0, error: 0 });
  const [messageCount, setMessageCount] = useState(0);
  const [brandEmailProjectId, setBrandEmailProjectId] = useState('');
  const [brandEmailInput, setBrandEmailInput] = useState('');
  const [loadedBrandEmail, setLoadedBrandEmail] = useState('');
  const [loadedBrandEmailProjectId, setLoadedBrandEmailProjectId] = useState('');
  const [brandEmailMessage, setBrandEmailMessage] = useState('');
  const [brandEmailStatus, setBrandEmailStatus] = useState<'success' | 'error' | 'neutral'>('neutral');
  const [isBrandEmailLoading, setIsBrandEmailLoading] = useState(false);
  const [isBrandEmailSubmitting, setIsBrandEmailSubmitting] = useState(false);
  const [isUpdatingPreviewForceStop, setIsUpdatingPreviewForceStop] = useState(false);
  const [isUpdatingQueueForceStop, setIsUpdatingQueueForceStop] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) {
      setQueueData({});
      setQueuePreviewData({});
      setLogCounts({ warning: 0, error: 0 });
      setMessageCount(0);
      return;
    }

    const queueRef = ref(database, 'Queue');
    const queuePreviewRef = ref(database, 'QueuePreview');
    const logEventsRef = ref(database, 'LogAnalytics/events');
    const messagesRef = ref(database, 'Messages');

    const unsubscribeQueue = onValue(queueRef, (snapshot) => {
      const value = snapshot.val() as QueueNode | null;
      setQueueData(value ?? {});
    });
    const unsubscribeQueuePreview = onValue(queuePreviewRef, (snapshot) => {
      const value = snapshot.val() as QueuePreviewNode | null;
      setQueuePreviewData(value ?? {});
    });
    const unsubscribeLogEvents = onValue(logEventsRef, (snapshot) => {
      setLogCounts(getLogCounts(snapshot.val()));
    });
    const unsubscribeMessages = onValue(messagesRef, (snapshot) => {
      const value = snapshot.val();
      setMessageCount(value && typeof value === 'object' ? Object.keys(value).length : 0);
    });

    return () => {
      unsubscribeQueue();
      unsubscribeQueuePreview();
      unsubscribeLogEvents();
      unsubscribeMessages();
    };
  }, [user]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError('Credenciales inválidas. Revisa correo y contraseña.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
  };

  const handleLoadBrandEmail = async () => {
    const normalizedProjectId = brandEmailProjectId.trim();
    if (!normalizedProjectId) {
      setBrandEmailStatus('error');
      setBrandEmailMessage('Missing or invalid field: projectId');
      return;
    }

    if (!user) {
      setBrandEmailStatus('error');
      setBrandEmailMessage('Unauthorized');
      return;
    }

    setIsBrandEmailLoading(true);
    setBrandEmailStatus('neutral');
    setBrandEmailMessage('Cargando email...');

    try {
      const projectSnapshot = await get(ref(database, `Projects/${normalizedProjectId}`));
      if (!projectSnapshot.exists()) {
        setBrandEmailStatus('error');
        setBrandEmailMessage('El proyecto no existe.');
        return;
      }

      const projectValue = (projectSnapshot.val() ?? {}) as { Email?: unknown };
      const email = String(projectValue.Email ?? '').trim();
      if (!email) {
        setBrandEmailStatus('error');
        setBrandEmailMessage('El proyecto no tiene email en RTDB.');
        return;
      }

      setBrandEmailInput(email);
      setLoadedBrandEmail(email);
      setLoadedBrandEmailProjectId(normalizedProjectId);
      setBrandEmailStatus('success');
      setBrandEmailMessage('Email cargado correctamente.');
    } catch (error) {
      setBrandEmailStatus('error');
      setBrandEmailMessage('No se pudo cargar el email.');
    } finally {
      setIsBrandEmailLoading(false);
    }
  };

  const handleSendBrandEmail = async () => {
    const normalizedProjectId = brandEmailProjectId.trim();
    const normalizedEmailInput = brandEmailInput.trim();
    const normalizedLoadedEmail = loadedBrandEmail.trim();
    const normalizedLoadedEmailForCompare = normalizedLoadedEmail.toLowerCase();
    const normalizedInputForCompare = normalizedEmailInput.toLowerCase();

    if (!normalizedProjectId) {
      setBrandEmailStatus('error');
      setBrandEmailMessage('Missing or invalid field: projectId');
      return;
    }

    if (!user) {
      setBrandEmailStatus('error');
      setBrandEmailMessage('Unauthorized');
      return;
    }

    if (loadedBrandEmailProjectId !== normalizedProjectId || !normalizedLoadedEmail) {
      setBrandEmailStatus('error');
      setBrandEmailMessage('Primero carga el email del proyecto.');
      return;
    }

    setIsBrandEmailSubmitting(true);
    setBrandEmailStatus('neutral');
    setBrandEmailMessage('Enviando...');

    try {
      const token = await user.getIdToken();
      const body =
        normalizedInputForCompare === normalizedLoadedEmailForCompare
          ? { projectId: normalizedProjectId }
          : { projectId: normalizedProjectId, email: normalizedEmailInput };

      const response = await fetch(sendBrandEmailEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const payload = (await response.json().catch(() => ({}))) as { error?: unknown };
      const errorMessage =
        typeof payload.error === 'string' && payload.error.trim() ? payload.error.trim() : '';

      if (!response.ok) {
        setBrandEmailStatus('error');
        setBrandEmailMessage(errorMessage || `Error ${response.status}`);
        return;
      }

      setBrandEmailStatus('success');
      setBrandEmailMessage('Email enviado correctamente.');
      setBrandEmailProjectId('');
      setBrandEmailInput('');
      setLoadedBrandEmail('');
      setLoadedBrandEmailProjectId('');
    } catch (error) {
      setBrandEmailStatus('error');
      setBrandEmailMessage('No se pudo enviar el email.');
    } finally {
      setIsBrandEmailSubmitting(false);
    }
  };

  const previewQueueCount = toQueueCount(queuePreviewData.projects);
  const globalQueueCount = toQueueCount(queueData.projectsGlobal);
  const standardQueueCount = toQueueCount(queueData.projectsStandard);
  const isPreviewQueueActive = Boolean(queuePreviewData.active);
  const isQueueActive = Boolean(queueData.active);
  const isPreviewForceStop = Boolean(queuePreviewData.forceStop);
  const isQueueForceStop = Boolean(queueData.forceStop);
  const previewRunInfo = getRunProjectInfo(queuePreviewData.run);
  const fullRunInfo = getRunProjectInfo(queueData.run);

  const handleToggleForceStop = async (path: 'Queue' | 'QueuePreview', nextValue: boolean) => {
    if (!user) {
      return;
    }

    const setLoading = path === 'QueuePreview' ? setIsUpdatingPreviewForceStop : setIsUpdatingQueueForceStop;
    setLoading(true);
    try {
      await update(ref(database, path), { forceStop: nextValue });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <div className="app-shell">
        {!user ? (
          <div className="card">
            <h1>WeDoIt Admin</h1>
            <form className="form" onSubmit={handleSubmit}>
              <label className="field">
                <span>Correo electrónico</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="admin@wedoit.com"
                  required
                />
              </label>
              <label className="field">
                <span>Contraseña</span>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  required
                />
              </label>
              {error ? <div className="error">{error}</div> : null}
              <button className="primary-button" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Validando...' : 'Iniciar sesión'}
              </button>
            </form>
          </div>
        ) : (
          <div className="panel">
            <div className="panel-header">
              <div>
                <div className="panel-title-row">
                  <h1>WeDoIt Branding Admin Panel</h1>
                  <button className="secondary-button" type="button" onClick={handleSignOut}>
                    Cerrar sesión
                  </button>
                </div>
                <div className="queue-dashboard">
                  <div className="queue-card">
                    <div className="queue-card-title">Colas</div>
                    <div className="queue-subcards queue-subcards-three">
                      <div className="queue-subcard">
                        <div className="queue-subcard-label">Preview</div>
                        <div className="queue-subcard-value">{previewQueueCount}</div>
                      </div>
                      <div className="queue-subcard">
                        <div className="queue-subcard-label">Standard</div>
                        <div className="queue-subcard-value">{standardQueueCount}</div>
                      </div>
                      <div className="queue-subcard">
                        <div className="queue-subcard-label">Global</div>
                        <div className="queue-subcard-value">{globalQueueCount}</div>
                      </div>
                    </div>
                  </div>

                  <div className={`queue-card ${isPreviewQueueActive ? 'queue-card-active' : 'queue-card-stopped'}`}>
                    <div className="queue-card-head">
                      <div className="queue-card-title">Preview</div>
                      <div className={`queue-bubble ${isPreviewQueueActive ? 'queue-bubble-active' : 'queue-bubble-stopped'}`}>
                        {isPreviewQueueActive ? 'CORRIENDO' : 'DETENIDA'}
                      </div>
                    </div>
                    <label className={`queue-force-stop ${isPreviewForceStop ? 'queue-force-stop-enabled' : ''}`}>
                      <span className="queue-force-stop-copy">
                        <span className="queue-force-stop-title">Forzar detener</span>
                        <span className="queue-force-stop-state">
                          {isUpdatingPreviewForceStop ? 'Guardando...' : isPreviewForceStop ? 'ACTIVO' : 'INACTIVO'}
                        </span>
                      </span>
                      <span className="queue-force-stop-toggle-wrap">
                        <input
                          type="checkbox"
                          className="queue-force-stop-input"
                          checked={isPreviewForceStop}
                          onChange={(event) => handleToggleForceStop('QueuePreview', event.target.checked)}
                          disabled={isUpdatingPreviewForceStop}
                        />
                        <span className="queue-force-stop-toggle" aria-hidden="true" />
                      </span>
                    </label>
                    <div className="queue-subcards">
                      <div className="queue-subcard">
                        <div className="queue-subcard-label">Proyecto</div>
                        <div className="queue-subcard-value queue-subcard-mono">
                          {previewRunInfo?.projectId || '-'}
                        </div>
                      </div>
                      <div className="queue-subcard">
                        <div className="queue-subcard-label">Prompt</div>
                        <div className="queue-subcard-value">
                          {previewRunInfo ? `${previewRunInfo.step}` : '-'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`queue-card ${isQueueActive ? 'queue-card-active' : 'queue-card-stopped'}`}>
                    <div className="queue-card-head">
                      <div className="queue-card-title">Full</div>
                      <div className={`queue-bubble ${isQueueActive ? 'queue-bubble-active' : 'queue-bubble-stopped'}`}>
                        {isQueueActive ? 'CORRIENDO' : 'DETENIDA'}
                      </div>
                    </div>
                    <label className={`queue-force-stop ${isQueueForceStop ? 'queue-force-stop-enabled' : ''}`}>
                      <span className="queue-force-stop-copy">
                        <span className="queue-force-stop-title">Forzar detener</span>
                        <span className="queue-force-stop-state">
                          {isUpdatingQueueForceStop ? 'Guardando...' : isQueueForceStop ? 'ACTIVO' : 'INACTIVO'}
                        </span>
                      </span>
                      <span className="queue-force-stop-toggle-wrap">
                        <input
                          type="checkbox"
                          className="queue-force-stop-input"
                          checked={isQueueForceStop}
                          onChange={(event) => handleToggleForceStop('Queue', event.target.checked)}
                          disabled={isUpdatingQueueForceStop}
                        />
                        <span className="queue-force-stop-toggle" aria-hidden="true" />
                      </span>
                    </label>
                    <div className="queue-subcards">
                      <div className="queue-subcard">
                        <div className="queue-subcard-label">Proyecto</div>
                        <div className="queue-subcard-value queue-subcard-mono">
                          {fullRunInfo?.projectId || '-'}
                        </div>
                      </div>
                      <div className="queue-subcard">
                        <div className="queue-subcard-label">Prompt</div>
                        <div className="queue-subcard-value">
                          {fullRunInfo ? `${fullRunInfo.step}` : '-'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="queue-card log-count-card">
                    <div className="queue-card-title">Backend</div>
                    <div className="log-count-grid">
                      <div className="log-count-box log-count-box-error">
                        <div className="log-count-label">Errores</div>
                        <div className="log-count-value">{logCounts.error}</div>
                      </div>
                      <div className="log-count-box log-count-box-warning">
                        <div className="log-count-label">Warnings</div>
                        <div className="log-count-value">{logCounts.warning}</div>
                      </div>
                      <div className="log-count-box log-count-box-neutral">
                        <div className="log-count-label">Mensajes</div>
                        <div className="log-count-value">{messageCount}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="panel-actions">
              <button
                className={`ghost-button ${activeSection === 'manual_generation' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('manual_generation')}
              >
                Generación manual
              </button>
              <button
                className={`ghost-button ${activeSection === 'create_project' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('create_project')}
              >
                Crear nuevo proyecto
              </button>
              <button
                className={`ghost-button ${activeSection === 'pricing' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('pricing')}
              >
                Modelos Gemini
              </button>
              <button
                className={`ghost-button ${activeSection === 'projects' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('projects')}
              >
                Detalle Proyecto
              </button>
              <button
                className={`ghost-button ${activeSection === 'enqueue_project' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('enqueue_project')}
              >
                Encolar proyecto
              </button>
              <button
                className={`ghost-button ${activeSection === 'prompt_test' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('prompt_test')}
              >
                Probar Prompt
              </button>
              <button
                className={`ghost-button ${activeSection === 'messages' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('messages')}
              >
                Mensajes Contacto
              </button>
              <button
                className={`ghost-button ${activeSection === 'brand_generation' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('brand_generation')}
              >
                Flujo Marca
              </button>
              <button
                className={`ghost-button ${activeSection === 'metrics' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('metrics')}
              >
                Métricas y Costos
              </button>
              <button
                className={`ghost-button ${activeSection === 'project_revenue' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('project_revenue')}
              >
                Ingresos Proyectos
              </button>
              <button
                className={`ghost-button ${activeSection === 'send_brand' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('send_brand')}
              >
                Enviar marca
              </button>
              <button
                className={`ghost-button ${activeSection === 'click_analytics' ? 'ghost-button-active' : ''}`}
                onClick={() => setActiveSection('click_analytics')}
              >
                Analíticas
              </button>
            </div>
            {activeSection === 'pricing' ? <PricingSection /> : null}
            {activeSection === 'prompt_test' ? <PromptTestSection /> : null}
            {activeSection === 'messages' ? <MessagesSection /> : null}
            {activeSection === 'projects' ? <ProjectsSection /> : null}
            {activeSection === 'manual_generation' ? <ManualGenerationSection /> : null}
            {activeSection === 'create_project' ? <CreateProjectSection /> : null}
            {activeSection === 'enqueue_project' ? <EnqueueProjectSection /> : null}
            {activeSection === 'brand_generation' ? <BrandGenerationSection /> : null}
            {activeSection === 'metrics' ? <MetricsSection /> : null}
            {activeSection === 'project_revenue' ? <ProjectRevenueSection /> : null}
            {activeSection === 'click_analytics' ? <ClickAnalyticsSection /> : null}
            {activeSection === 'send_brand' ? (
              <div className="section">
                <div className="section-header">
                  <h2>Enviar marca</h2>
                </div>
                <form
                  className="brand-email-form brand-email-section-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (loadedBrandEmailProjectId === brandEmailProjectId.trim() && loadedBrandEmail.trim()) {
                      void handleSendBrandEmail();
                      return;
                    }
                    void handleLoadBrandEmail();
                  }}
                >
                  <label className="field">
                    <span>Project ID</span>
                    <input
                      type="text"
                      placeholder="projectId"
                      value={brandEmailProjectId}
                      onChange={(event) => {
                        setBrandEmailProjectId(event.target.value);
                        setBrandEmailInput('');
                        setLoadedBrandEmail('');
                        setLoadedBrandEmailProjectId('');
                      }}
                      disabled={isBrandEmailLoading || isBrandEmailSubmitting}
                      required
                    />
                  </label>
                  <label className="field">
                    <span>Email</span>
                    <input
                      type="email"
                      placeholder="email"
                      value={brandEmailInput}
                      onChange={(event) => setBrandEmailInput(event.target.value)}
                      disabled={isBrandEmailLoading || isBrandEmailSubmitting}
                    />
                  </label>
                  <div className="brand-email-actions">
                    <button
                      className="secondary-button"
                      type="submit"
                      disabled={isBrandEmailLoading || isBrandEmailSubmitting}
                    >
                      {isBrandEmailLoading
                        ? 'Cargando...'
                        : isBrandEmailSubmitting
                          ? 'Enviando...'
                          : loadedBrandEmailProjectId === brandEmailProjectId.trim() && loadedBrandEmail.trim()
                            ? 'Enviar'
                            : 'Cargar'}
                    </button>
                    {brandEmailMessage ? (
                      <div
                        className={`inline-toast ${
                          brandEmailStatus === 'success'
                            ? 'inline-toast-success'
                            : brandEmailStatus === 'neutral'
                              ? 'inline-toast-neutral'
                              : 'inline-toast-error'
                        }`}
                      >
                        {brandEmailMessage}
                      </div>
                    ) : null}
                  </div>
                </form>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
